import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Box } from "@mui/material";
import {
  EntityTags,
  Inline,
  Label,
  LinearProgress,
  TagList,
  TagSearch,
  TagSelector,
} from "@gpstrackit/gtc-react-controls";
import { useTagsManage } from "./useTagsManage";

const TAG_MODE_INHERITANCE = "Inheritance";

const TagsManage = ({
  entities,
  entitySelected,
  setEntitySelected,
  value,
  onChange,
  multiple = true,
  label,
  height,
  isVisiblePill = false,
  showCount = false,
  placeholder,
  mode,
  onlyTags = false,
  enableSelectAll = false,
  disableUntaggedSelection = false,
  disableTagSelection = false,
  hideTagsWithoutEntities = true,
  disableInheritance = false,
  onlyCheckEntities = false,
  menuStyle,
  includeInactiveDrivers = false,
  required = false,
  requiredMessage,
  showError = false,
  requiredAsteriskPosition = "end", // start | end | none
  labelFontSize = 14, // string | number
  videoUnits = false,
  deviceModelSelected = null,
  ...rest
}) => {

  const { messages } = useIntl();

  const { drivers, units, tags, forms, subusers, landmarks, geofences, assets, isLoading, user, allPermissionsTags } = useTagsManage({ onlyTags, videoUnits, deviceModelSelected });

  const isInheritance = user?.tagMode === TAG_MODE_INHERITANCE && !disableInheritance;
  const [showInactiveDrivers, setShowInactiveDrivers] = useState(true);
  const [filteringTags, setFilteringTags] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [entitySearchText, setEntitySearchText] = useState("");

  useEffect(() => {
    setFilteringTags(tags);
  }, [tags]);


  const driversFiltered = !showInactiveDrivers ? drivers : drivers?.filter((driver) => driver?.status === 1);

  const matchesEntityText = (item, lowerText) => item?.label?.toLowerCase()?.includes(lowerText) || item?.name?.toLowerCase()?.includes(lowerText);

  const onFilteringTags = (value) => {
    const text = value || "";
    const lowerText = text.toLowerCase();
    let nextFilteringTags = tags;
    let nextEntitySearchText = "";

    const tagFilters = tags?.filter((x) => x?.label?.toLowerCase()?.includes(lowerText));

    if (tagFilters.length) {
      const tagFiltersChildrens = tags?.filter((x) =>tagFilters?.find((y) => x?.tagPath?.startsWith(`${y?.tagPath}/`)));
      let tagFiltersIds = tagFilters?.map((x) => x?.tagPath?.split("/").join(",")).join(",")?.split(",");

      if (tagFiltersChildrens?.length) {
        tagFiltersIds = tagFiltersIds?.concat(
          ...tagFiltersIds,
          ...tagFiltersChildrens?.map((x) => x?.id?.toString())
        );
      }

      nextFilteringTags = tags?.filter((x) => tagFiltersIds?.includes(x?.id?.toString()));
    } else if (lowerText) {
      
      const entityCollections = [units, driversFiltered, landmarks, geofences, forms, subusers, assets];
      const hasEntityMatch = entityCollections?.some((collection) => collection?.some((item) => matchesEntityText(item, lowerText)));

      if (hasEntityMatch) {
        nextFilteringTags = tags;
        nextEntitySearchText = text;
      } else {
        nextFilteringTags = [];
      }
    }

    setFilteringTags(nextFilteringTags);
    setSearchText(text);
    setEntitySearchText(nextEntitySearchText);
  };

   const hasError =
    required &&
    ((multiple && (!value || value.length === 0)) ||
      (!multiple && !value)) &&
    showError;
    
    const renderLabel = () => {
      if (!label) return null;

      const showStar = required && requiredAsteriskPosition !== "none";

      return (
        <Box
          sx={{
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            fontSize: labelFontSize,
            mb: 0.5,
          }}
        >
          {showStar && requiredAsteriskPosition === "start" && (
            <Box component="span" sx={{ color: "red", mr: 0.5 }}>
              *
            </Box>
          )}

          <Box component="span">{label}</Box>

          {showStar && requiredAsteriskPosition === "end" && (
            <Box component="span" sx={{ color: "red", ml: 0.5 }}>
              *
            </Box>
          )}
        </Box>
      );
    };

  const getCommonTagSelectorProps = () => ({
    enableSelectAll: allPermissionsTags && enableSelectAll,
    value,
    disableUntaggedSelection,
    disableTagSelection,
    multiple,
    onChange,
    tags: filteringTags,
    units,
    drivers: driversFiltered,
    landmarks,
    geofences,
    forms,
    subusers,
    hideTagsWithoutEntities,
    messages,
    height,
    includeInactiveDrivers,
    showCount,
    inheritance: isInheritance,
    menuStyle,
    setSearchText: onFilteringTags,
    placeholder: placeholder,
    showInactiveDrivers: showInactiveDrivers,
    handleIncludeInactiveDriversChange: handleIncludeInactiveDriversChange,
    placeholder: messages["search"],
    hasError,
  });

  const handleIncludeInactiveDriversChange = () => {
    setShowInactiveDrivers(!showInactiveDrivers);
  }


  if (mode === "inline") {
    return (
      <Box {...rest} data-testid="tags-manage-inline">
       <TagSearch
        loading={isLoading}
        onChange={onFilteringTags}
        placeholder={messages["search"]}
        value={value}
        entity={entitySelected?.id || "Unit"}
       />
        <Inline
          mode="inline"
          tags={filteringTags}
          value={value}
          onChange={onChange}
          disableUntaggedSelection={disableUntaggedSelection}
        />
      </Box>
    );
  }

  if (onlyTags) {
    return (
      <Box {...rest} data-testid="tags-manage-only-tags">
        {mode === "selector" ? (
          <>
            {renderLabel()}

            <TagSelector
              {...getCommonTagSelectorProps()}
              entity={entitySelected?.id}
              values={isVisiblePill ? value : undefined}
              assets={assets}
              onlyTags={onlyTags}
              isVisiblePill={isVisiblePill}
            />
            {isLoading && <LinearProgress />}
          </>
        ) : (
          <>
            {renderLabel()}

            <TagSearch
              loading={isLoading}
              onChange={onFilteringTags}
              placeholder={messages["search"]}
              value={value}
              entity={entitySelected?.id}
            />
            {isLoading && <LinearProgress />}
            <TagList
              onlyCheckEntities={onlyCheckEntities}
              enableSelectAll={allPermissionsTags && enableSelectAll}
              entity={entitySelected?.id}
              placeholder={messages["search"]}
              value={value}
              disableUntaggedSelection={disableUntaggedSelection}
              disableTagSelection={disableTagSelection}
              multiple={multiple}
              onChange={onChange}
              tags={filteringTags}
              units={units}
              drivers={driversFiltered}
              landmarks={landmarks}
              geofences={geofences}
              forms={forms}
              subusers={subusers}
              hideTagsWithoutEntities={hideTagsWithoutEntities}
              messages={messages}
              height={height}
              includeInactiveDrivers={includeInactiveDrivers}
              assets={assets}
              onlyTags={onlyTags}
              showCount={showCount}
              inheritance={isInheritance}
            />
          </>
        )}
      </Box>
    );
  }

  if (mode === "selector") {
    return (
      <Box {...rest} data-testid="tags-manage-selector">
        <EntityTags
          entityTagsList={entities}
          selectedEntity={entitySelected}
          setSelectedEntity={setEntitySelected}
        />
        {entitySelected?.id !== "All" && (
          <>
            <TagSelector
              {...getCommonTagSelectorProps()}
              entity={entitySelected?.id}
              onlyTags={onlyTags}
              values={value}
              isVisiblePill={isVisiblePill}
            />
            {isLoading && <LinearProgress />}
          </>
        )}
      </Box>
    );
  }

  return (
    <Box {...rest} data-testid="tags-manage-default">
      {renderLabel()}

      <EntityTags
        entityTagsList={entities}
        selectedEntity={entitySelected}
        setSelectedEntity={(entity) => setEntitySelected(entity)}
      />
      {entitySelected?.id !== "All" && (
        <>
          <TagSearch
            loading={isLoading}
            onChange={onFilteringTags}
            placeholder={messages["search"]}
            value={searchText}
            entity={entitySelected?.id}
          />
          {isLoading && <LinearProgress />}
          <TagList
            onlyCheckEntities={onlyCheckEntities}
            enableSelectAll={allPermissionsTags && enableSelectAll}
            entity={entitySelected?.id}
            value={value}
            disableUntaggedSelection={disableUntaggedSelection}
            disableTagSelection={disableTagSelection}
            multiple={multiple}
            onChange={onChange}
            tags={filteringTags}
            units={units}
            drivers={driversFiltered}
            landmarks={landmarks}
            geofences={geofences}
            forms={forms}
            subusers={subusers}
            hideTagsWithoutEntities={hideTagsWithoutEntities}
            messages={messages}
            height={height}
            includeInactiveDrivers={includeInactiveDrivers}
            assets={assets}
            onlyTags={onlyTags}
            showCount={showCount}
            inheritance={isInheritance}
            searchText={entitySearchText}
          />
        </>
      )}
    </Box>
  );
};

export default TagsManage;